import React, { useEffect, useRef } from 'react';
import { CircleAlert, Clock, CreditCard, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../shared/components';
import { useStore } from '../../context/StoreContext';
import { useStorefrontTheme } from '../../shared/theme/storefrontTheme';
import {
  FINIX_ERROR_MESSAGES,
  GOOGLE_RECAPTCHA_ERROR_MESSAGES,
} from '../../utilities/constant';
import '../Checkout/checkout.css';
import '../../shared/components/organisms/Browse/browse.css';
import '../OrderConfirmation/orderConfirmation.css';

const CARD_FALLBACK = FINIX_ERROR_MESSAGES.DEFAULT;
const ORDER_FALLBACK = 'Something went wrong on our side while placing your order. Please try again in a moment.';

// Server text that tells a shopper nothing ("Internal Server Error", a JS crash).
const UNHELPFUL = /^(internal server error|bad request|unprocessable entity|cannot read propert|undefined|null)/i;
const CARD_HINT = /declin|card|cvv|cvc|expir|insufficient|issuer|token is invalid|payment method/i;
const SECURITY_HINT = /recaptcha|security verification/i;

/* The order API answers `{ code: <HTTP status>, errors }`. `errors` is either a
   Finix JSON list (with `failure_code`) or plain text, sometimes repeated per cart
   (" , No Active Shop Token Found , No Active Shop Token Found"). */
export const readOrderError = (data, status) => {
  const raw = data?.errors ?? data?.message;
  let failureCode;
  let text = '';

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      const first = Array.isArray(parsed) ? parsed[0] : parsed;
      failureCode = first?.failure_code;
      text = first?.failure_message || first?.message || '';
    } catch (e) {
      text = raw;
    }
  } else if (Array.isArray(raw)) {
    text = raw.map((r) => r?.message || r).join(', ');
  }

  let message = [...new Set(String(text).split(',').map((s) => s.trim()).filter(Boolean))].join('. ');
  // Finix's duplicate-request answer names internal ids; say what it means instead.
  if (/duplicate|idempotency/i.test(message)) message = 'This payment was already tried. Please try again';

  return {
    status,
    failureCode: status === 429 ? 'LIMIT_EXCEEDED' : failureCode,
    message: UNHELPFUL.test(message) ? '' : message,
  };
};

const sentence = (text) => (/[.!?]$/.test(text) ? text : `${text}.`);

const describe = (error = {}) => {
  const { failureCode, message, status } = error;

  if (failureCode === 'LIMIT_EXCEEDED') {
    return { Icon: Clock, title: 'Too many attempts', body: FINIX_ERROR_MESSAGES.LIMIT_EXCEEDED, action: 'OK' };
  }
  if (GOOGLE_RECAPTCHA_ERROR_MESSAGES[failureCode] || SECURITY_HINT.test(message)) {
    return {
      Icon: ShieldAlert,
      title: 'We couldn’t verify this checkout',
      body: GOOGLE_RECAPTCHA_ERROR_MESSAGES[failureCode] || 'The security check didn’t pass. Refresh the page and try again.',
      action: 'Try again',
    };
  }
  if (FINIX_ERROR_MESSAGES[failureCode] || status === 402 || CARD_HINT.test(message)) {
    return {
      Icon: CreditCard,
      title: 'Your card was declined',
      body: (failureCode && FINIX_ERROR_MESSAGES[failureCode]) || (message && sentence(message)) || CARD_FALLBACK,
      action: 'Try another card',
    };
  }
  return {
    Icon: CircleAlert,
    title: 'We couldn’t place your order',
    body: message ? sentence(message) : ORDER_FALLBACK,
    action: 'Try again',
  };
};

/* The order-failure card: an alert dialog over the checkout, worded from what
   actually failed. Without `setShowError` (the /order-failure route) it is a page
   card that leads back to the cart. */
const OrderFailure = ({ setShowError, error }) => {
  const { Icon, title, body, action } = describe(error);
  const cardRef = useRef(null);
  const isDialog = typeof setShowError === 'function';

  useEffect(() => {
    if (!isDialog) return undefined;
    cardRef.current?.querySelector('button')?.focus();
    const onKey = (e) => { if (e.key === 'Escape') setShowError(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isDialog, setShowError]);

  return (
    <div
      ref={cardRef}
      className="co-fail"
      role={isDialog ? 'alertdialog' : 'alert'}
      aria-modal={isDialog || undefined}
      aria-labelledby="co-fail-title"
      aria-describedby="co-fail-body"
    >
      <span className="co-fail-icon" aria-hidden="true"><Icon /></span>
      <h3 id="co-fail-title">{title}</h3>
      <p id="co-fail-body">{body}</p>
      <div className="co-fail-actions">
        {isDialog ? (
          <Button onClick={() => setShowError(false)}>{action}</Button>
        ) : (
          <Button href="/viewcart">Back to your cart</Button>
        )}
      </div>
      <p className="co-fail-note">If this keeps happening, contact the store.</p>
    </div>
  );
};

/* The /order-failure route: the same layout as the confirmation page's
   "We can't find that order" (crumb + `.br-empty` box), so the two read as one
   family. Inside checkout the failure is the dialog above, not this page. */
const OrderFailurePage = ({ error }) => {
  const { pageDetails } = useStore();
  const sfxRef = useStorefrontTheme(pageDetails?.page_data?.colorAndApperances);
  const { title, body } = describe(error);
  return (
    <div className="sfx" ref={sfxRef}>
      {/* Layout on an inner box: the theme hook rewrites the outer element's className. */}
      <div className="br-wrap">
        <nav className="br-crumb" aria-label="Breadcrumb"><Link to="/">Home</Link> / Order</nav>
        <div className="br-empty oc-missing" role="alert">
          <h3>{title}</h3>
          <p>{body}</p>
          <Button href="/viewcart">Back to your cart</Button>
        </div>
      </div>
    </div>
  );
};

export { OrderFailure };
export default OrderFailurePage;
